// The app frame (rail, top bar, content panel, phone tab bar, omnibar, shortcuts) and the bare layout.
import { useEffect, useSyncExternalStore, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Navigate, Outlet, useRouter, useRouterState } from '@tanstack/react-router'
import { WifiOff } from 'lucide-react'
import { Omnibar } from '../features/index.ts'
import { onUnauthenticated } from '../lib/api.ts'
import { useEventStream } from '../lib/events.ts'
import { useAccountLocale, useT } from '../lib/i18n.tsx'
import { useUpdateReady } from '../lib/pwa.ts'
import { sessionQuery, type SessionState } from '../lib/session.ts'
import { useToast } from '../kit/Toast.tsx'
import { usePhone } from '../kit/util.ts'
import { installKeys } from './keys.ts'
import { entryFor } from './nav.ts'
import { NotFoundScreen, OfflineScreen, PendingScreen, RouteSkeleton } from './screens.tsx'
import { EnvBadge } from './shell/EnvBadge.tsx'
import { Rail } from './shell/Rail.tsx'
import { ShortcutsSheet } from './shell/ShortcutsSheet.tsx'
import { TabBar } from './shell/TabBar.tsx'
import { TopBar } from './shell/TopBar.tsx'
import { closeOverlay, openOverlay, toggleOverlay, useOverlay } from './ui.ts'

/** Once per page: the frame mounts again on every move between a screen and not-found and the effect runs again when the language changes, while the toast stays until Reload or Dismiss. */
let updateOffered = false

/** Offers the waiting service-worker version once per page, as a toast with Reload. */
function UpdateToast() {
  const t = useT()
  const toast = useToast()
  const update = useUpdateReady()
  useEffect(() => {
    if (!update || updateOffered) return
    updateOffered = true
    toast.show({ text: t('common.updateReady'), action: { label: t('common.reload'), run: update } })
  }, [update, toast, t])
  return null
}

/** True while the browser has a connection (the same rule as Today's own banner). */
function useOnline(): boolean {
  return useSyncExternalStore(
    (notify) => {
      window.addEventListener('online', notify)
      window.addEventListener('offline', notify)
      return () => {
        window.removeEventListener('online', notify)
        window.removeEventListener('offline', notify)
      }
    },
    () => navigator.onLine,
    () => true,
  )
}

/** The path the last navigation ended on. Module-level: the frame mounts again between a screen and not-found, and the bare layout is another tree. */
let shownPath: string | null = null

/**
 * WCAG 2.4.2 and 2.4.3: once a navigation has resolved the page is titled '<screen> · Network' and focus moves to the
 * new screen's main once it is rendered (unless the screen or an open dialog already holds focus), so a keyboard or
 * screen-reader user starts at the new screen, and follows it when that main is replaced (a screen shows its loading state as
 * a <Screen> of its own, then the page) so focus never falls to the body. The title is the
 * rail label; a screen without one and an organisation page take their heading, which loads later (hence the observer).
 * A person page keeps the label: its heading can be a register name, which must not enter the browser history.
 */
function useScreenTitle(): void {
  const t = useT()
  const pathname = useRouterState({ select: (s) => s.resolvedLocation?.pathname })
  const entry = pathname === undefined ? undefined : entryFor(pathname)
  const label = entry ? t(entry.label) : undefined
  const app = t('common.appName')
  useEffect(() => {
    if (pathname === undefined) return
    const heading = !entry || pathname.startsWith('/orgs/')
    const set = () => {
      const name = (heading && document.querySelector('main h1')?.textContent?.trim()) || label
      document.title = !name || name.includes(app) ? name || app : `${name} · ${app}`
    }
    set()
    // A slow screen chunk resolves the path while its pending skeleton shows and the previous screen's main is still in
    // the page, hidden (display: none), where it takes no focus. So focus goes to a main that is rendered, which is the
    // new screen's, and until one is the observer waits for it (WS-P4C-SHELL605-FLAKE).
    let held: HTMLElement | null = null // the main focus is in or was moved to
    const focusMain = (): boolean => {
      const main = document.getElementById('main')
      if (!main || main.getClientRects().length === 0) return false
      const active = document.activeElement
      if (!main.contains(active) && !active?.closest('[aria-modal="true"], [cmdk-dialog]')) main.focus({ preventScroll: true })
      held = main
      return true
    }
    const moved = shownPath !== null && shownPath !== pathname
    shownPath = pathname
    const observers: MutationObserver[] = []
    if (moved) {
      // Until a main is rendered, and again when the one focus is in is replaced and focus fell to the body: the organisation
      // page and the person page swap their loading screen for the page, each its own main (WS-P5-FOCUS-SKELETON).
      focusMain()
      const following = new MutationObserver(() => {
        if (!held || (!held.isConnected && document.activeElement === document.body)) focusMain()
      })
      following.observe(document.body, { childList: true, subtree: true })
      observers.push(following)
    }
    if (heading) {
      const titles = new MutationObserver(set)
      titles.observe(document.body, { childList: true, subtree: true, characterData: true })
      observers.push(titles)
    }
    return () => observers.forEach((o) => o.disconnect())
  }, [pathname, entry, label, app])
}

export function FrameLayout({ session, children }: { session: SessionState; children: ReactNode }) {
  const t = useT()
  const router = useRouter()
  const queryClient = useQueryClient()
  const phone = usePhone()
  const online = useOnline()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const omnibarOpen = useOverlay('omnibar')
  const me = session.kind === 'user' ? session.me : null
  const fixture = session.kind === 'user' && session.fixture === true
  const active = me?.status === 'active'

  useAccountLocale(me?.locale)
  useEventStream(active && !fixture)
  useScreenTitle()

  useEffect(
    () =>
      installKeys({
        toggleOmnibar: () => active && toggleOverlay('omnibar'),
        openShortcuts: () => openOverlay('shortcuts'),
        go: (to) => void router.navigate({ to }),
        logTouch: () => active && void router.navigate({ to: '/touches', search: { log: 1 } as never }),
      }),
    [router, active],
  )

  // Any 401 from the API (stale cookie, logout elsewhere): re-check the session; the frame then sends us to /login.
  useEffect(
    () =>
      onUnauthenticated(() => {
        void queryClient.invalidateQueries({ queryKey: sessionQuery.queryKey }).then(() => router.invalidate())
      }),
    [queryClient, router],
  )

  return (
    <div className="flex min-h-dvh flex-col bg-canvas md:h-dvh md:overflow-hidden">
      <a
        href="#main"
        className="sr-only z-90 rounded-md bg-accent px-3 py-2 text-accent-ink focus:not-sr-only focus:fixed focus:top-2 focus:left-2"
      >
        {t('common.skipToContent')}
      </a>
      <div className="flex min-h-0 flex-1">
        <Rail />
        <div className="flex min-w-0 flex-1 flex-col md:py-2 md:pr-2">
          <div className="flex min-h-0 flex-1 flex-col bg-surface md:overflow-hidden md:rounded-xl md:shadow-raised md:ring-1 md:ring-line">
            <TopBar me={active ? me : null} fixture={fixture} />
            <div
              id="scroll-root"
              tabIndex={phone ? undefined : 0}
              className="flex min-h-0 flex-1 flex-col pb-tabbar-safe outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-inset md:overflow-y-auto md:pb-0"
            >
              {!online && me && pathname !== '/today' && (
                <div
                  role="status"
                  data-testid="offline-banner"
                  className="mx-4 mt-3 flex items-center gap-3 rounded-lg bg-sunken px-4 py-3 text-sm text-ink ring-1 ring-line-strong ring-inset md:mx-8"
                >
                  <WifiOff size={16} aria-hidden className="shrink-0" />
                  {t('common.offline.banner')}
                </div>
              )}
              {children}
            </div>
          </div>
        </div>
      </div>
      <TabBar search={active} />
      {active && <Omnibar open={omnibarOpen} onOpenChange={(open) => (open ? openOverlay('omnibar') : closeOverlay())} />}
      <ShortcutsSheet />
      <UpdateToast />
    </div>
  )
}

/** The pathless frame route's component: session states first, then the matched screen. */
export function FrameRoute() {
  const { data } = useQuery(sessionQuery)
  const session: SessionState = data ?? { kind: 'offline' }
  if (session.kind === 'signed-out') return <Navigate to="/login" replace />
  const screen =
    session.kind === 'offline' || session.kind === 'unavailable' ? (
      <OfflineScreen unavailable={session.kind === 'unavailable'} />
    ) : session.me.status !== 'active' ? (
      <PendingScreen />
    ) : (
      <Outlet />
    )
  return <FrameLayout session={session}>{screen}</FrameLayout>
}

/** Full-screen layout for sign-in and invite (features' `bare` routes) and for not-found without a session. */
export function BareLayout({ children }: { children: ReactNode }) {
  useScreenTitle()
  return (
    <div className="flex min-h-dvh flex-col bg-canvas glow-ground pt-safe">
      <div className="fixed top-3 right-3 z-40">
        <EnvBadge />
      </div>
      <div className="flex flex-1 flex-col">{children}</div>
    </div>
  )
}

export function BareRoute() {
  return (
    <BareLayout>
      <Outlet />
    </BareLayout>
  )
}

/** An unknown path: inside the frame for a signed-in person, bare otherwise (never a redirect loop to /login). */
export function RootNotFound() {
  const { data, isPending } = useQuery(sessionQuery)
  if (isPending) return <RouteSkeleton />
  if (data?.kind === 'user') {
    return (
      <FrameLayout session={data}>
        <NotFoundScreen />
      </FrameLayout>
    )
  }
  return (
    <BareLayout>
      <NotFoundScreen />
    </BareLayout>
  )
}
